import { Loader } from "@zfp-akademie-a-s/zfp-design";
import { type ReactNode, createContext, useContext, useEffect } from "react";

import { useUserProfile } from "Infrastructure/ApiZfp/AnalyzerApi";
import type { UserPermissionsItem as Permission, User, UserProfile } from "Infrastructure/ApiZfp/Schema";
import { trackUserIdentification } from "Infrastructure/Utils/TrackingUtils";
import { AppRouting } from "Infrastructure/Utils/UrlUtils";

interface UserContextType {
  user: UserProfile | null;
  hasPermission: (permission: Permission) => boolean;
  hasAnyPermission: (permissions: Permission[]) => boolean;
  hasPermissions: () => boolean;
  hasAllPermissions: (permissions: Permission[]) => boolean;
  getGuid: () => UserProfile["guid"] | null;
  loading: boolean;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

interface UserProviderProps {
  children: ReactNode;
}

export const UserContextProvider = ({ children }: UserProviderProps) => {
  // Analyzer SSO always lands on Dashboard, so the clients redirect must not trigger it and lose the bookmark.
  const isProfileSkipped = [AppRouting.OAuthCallback.path(), AppRouting.Clients.path()].includes(window.location.pathname);

  const { data, isLoading, isError, error } = useUserProfile({
    query: { retry: false, enabled: !isProfileSkipped },
  });

  const user = data ?? null;

  useEffect(() => {
    if (data) {
      trackUserIdentification();
    }
  }, [data]);

  if (isError) {
    const status = error && typeof error === "object" && "status" in error ? (error as { status: number }).status : undefined;
    if (status === 401) {
      return <Loader />; // triggerSsoLogin() redirect is in progress
    }
    throw error;
  }

  const getGuid = (): User["guid"] | null => {
    if (!user) return null;
    return user.guid;
  };

  const hasPermission = (permission: Permission): boolean => {
    if (!user) return false;
    return user.permissions.includes(permission);
  };

  const hasPermissions = (): boolean => Boolean(user?.permissions?.length);

  const hasAnyPermission = (requiredPermissions: Permission[]): boolean => {
    if (!user) return false;
    return requiredPermissions.some((permission) => user.permissions.includes(permission));
  };

  const hasAllPermissions = (requiredPermissions: Permission[]): boolean => {
    if (!user) return false;
    return requiredPermissions.every((permission) => user.permissions.includes(permission));
  };

  const value = {
    user,
    hasPermission,
    hasAnyPermission,
    hasPermissions,
    hasAllPermissions,
    getGuid,
    loading: isLoading,
  };

  if (isLoading) return <Loader />;

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

export const useUserContext = (): UserContextType => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error("useUserContext must be used within a UserProvider");
  }
  return context;
};
